import { useCallback, useEffect, useRef, useState } from "react";
import { byId, byKey, durationOf, tourAnimations } from "../animations/registry";
import { partyGroup } from "../animations/party";
import { listenForUnlock, stopSamples } from "../audio/audioEngine";
import { playCue } from "../audio/cues";
import { warmSamples } from "../audio/samples";
import { watchFrames } from "../utils/renderTier";

// Animatiemotor: houdt bij wat er speelt, met geluid, en ruimt het op. Elke
// run krijgt een eigen runId zodat een CSS-animatie echt herstart. Groepjes
// (een verkoop) staan in de rij; play() zelf speelt direct.

let counter = 0;

// Tussen twee animaties van hetzelfde groepje.
const stagger = 700;

// Stilte tussen twee verkopen, zodat ze niet in elkaar overlopen.
const groupPause = 3500;

// Maximaal aantal wachtende groepjes; daarboven vallen de nieuwste af.
const queueMax = 6;

// Licht de rijen van één verkoper op, gevonden via data-name.
function highlightSeller(name, ms) {
  if (!name) return () => {};

  let nodes = [];
  // Eén frame wachten tot de ranglijst op de nieuwe volgorde staat.
  const frame = requestAnimationFrame(() => {
    nodes = Array.from(
      document.querySelectorAll(`[data-name="${CSS.escape(name)}"]`),
    );
    nodes.forEach((node) => node.classList.add("fx-highlight"));
  });

  const clean = () => nodes.forEach((node) => node.classList.remove("fx-highlight"));
  const timer = setTimeout(clean, ms);

  return () => {
    cancelAnimationFrame(frame);
    clearTimeout(timer);
    clean();
  };
}

// Shift+8; via event.key, want event.code kent geen Shift.
const partyKey = "*";

// Toetsen negeren tijdens typen in een veld of met een modifier.
function ignoreKey(event) {
  const tag = event.target?.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return true;
  return event.metaKey || event.ctrlKey || event.altKey;
}

export function useAnimations({ keyboard = true, party = true } = {}) {
  const [active, setActive] = useState([]);
  const timers = useRef(new Map());

  // De wachtrij in refs: die hoeft niet opnieuw te tekenen.
  const queue = useRef([]);
  const groupTimers = useRef([]);
  const busy = useRef(false);

  const clear = useCallback((runId) => {
    const timer = timers.current.get(runId);
    if (timer) {
      clearTimeout(timer.id);
      timer.cleanup?.();
      timers.current.delete(runId);
    }
    setActive((list) => list.filter((run) => run.runId !== runId));
  }, []);

  const play = useCallback(
    (id, payload = {}) => {
      const animation = byId(id);
      if (!animation) return;

      const runId = ++counter;
      const ms = durationOf(animation, payload);

      // quiet: zonder eigen geluid; cue: een ander geluid bij hetzelfde beeld.
      if (!payload.quiet) playCue(payload.cue || id, payload);

      const cleanup =
        animation.target === "seller"
          ? highlightSeller(payload.name, ms)
          : null;

      timers.current.set(runId, {
        id: setTimeout(() => clear(runId), ms),
        cleanup,
      });
      setActive((list) => [...list, { runId, id, payload }]);
      return runId;
    },
    [clear],
  );

  const stopAll = useCallback(() => {
    // Ook lopende opnames stoppen.
    stopSamples();

    timers.current.forEach((timer) => {
      clearTimeout(timer.id);
      timer.cleanup?.();
    });
    timers.current.clear();

    groupTimers.current.forEach(clearTimeout);
    groupTimers.current = [];
    queue.current = [];
    busy.current = false;

    setActive([]);
  }, []);

  // play en runGroup verwijzen naar elkaar, dus via een ref.
  const self = useRef({});
  self.current.play = play;

  // Speelt een groepje [animatie, gegevens, start?] af; zonder start komt
  // een regel stagger na de vorige. Daarna is het volgende groepje aan de beurt.
  const runGroup = useCallback((events) => {
    busy.current = true;
    groupTimers.current = [];

    let total = 0;
    let previous = -stagger;
    events.forEach(([id, payload, at]) => {
      const start = at ?? previous + stagger;
      previous = start;
      const animation = byId(id);
      total = Math.max(total, start + (animation ? durationOf(animation, payload) : 0));
      groupTimers.current.push(
        setTimeout(() => self.current.play(id, payload), start),
      );
    });

    // busy blijft staan tot na de pauze.
    groupTimers.current.push(
      setTimeout(() => {
        busy.current = false;
        const next = queue.current.shift();
        if (next) self.current.runGroup(next);
      }, total + groupPause),
    );
  }, []);
  self.current.runGroup = runGroup;

  // Speelt direct of zet in de rij; keep: ook als de rij vol zit (inhalen).
  const playGroup = useCallback(
    (events, { keep = false } = {}) => {
      if (!events?.length) return;

      if (busy.current) {
        if (keep || queue.current.length < queueMax) queue.current.push(events);
        return;
      }

      runGroup(events);
    },
    [runGroup],
  );

  // Het feestje: alles stoppen en meteen beginnen.
  const playParty = useCallback(() => {
    stopAll();
    playGroup(partyGroup());
  }, [playGroup, stopAll]);

  // Rondleiding: alle animaties achter elkaar met voorbeeldgegevens.
  const playAll = useCallback(() => {
    stopAll();
    let wait = 0;
    tourAnimations.forEach((animation) => {
      setTimeout(() => play(animation.id, animation.demo || {}), wait);
      wait += Math.min(animation.ms, 3000) + 400;
    });
  }, [play, stopAll]);

  // Shift+8 werkt ook als de testtoetsen uit staan.
  useEffect(() => {
    if (!party) return undefined;

    const onKey = (event) => {
      if (!ignoreKey(event) && event.key === partyKey) playParty();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [party, playParty]);

  useEffect(() => {
    if (!keyboard) return undefined;

    const onKey = (event) => {
      if (ignoreKey(event)) return;

      if (event.key === "Escape") return stopAll();
      if (event.key === " ") {
        event.preventDefault();
        return playAll();
      }

      const animation = byKey(event.key);
      if (animation) play(animation.id, animation.demo || {});
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keyboard, play, playAll, stopAll]);

  useEffect(() => () => stopAll(), [stopAll]);

  // Geluidsbestanden vooraf ophalen en luisteren naar de eerste aanraking.
  useEffect(() => {
    warmSamples();
    return listenForUnlock();
  }, []);

  // Framerate meten zolang er iets beweegt (utils/renderTier.js).
  const busyNow = active.length > 0;
  useEffect(() => {
    if (!busyNow) return undefined;
    return watchFrames();
  }, [busyNow]);

  // Klassen voor animaties die een bestaand onderdeel aansturen.
  const rootClass = active.map((run) => `anim-${run.id}`).join(" ");

  return { active, play, playGroup, playParty, playAll, stopAll, rootClass };
}
